import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Image as ImageIcon,
  Layers,
  Package,
  Plus,
  Tag,
  Upload,
  X,
} from 'lucide-react';
import { getCurrencySymbol, shopifyApi } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { cn } from '@/lib/utils';

/**
 * The Shopify create-product form — state, submit and fields — extracted from
 * `CreateShopifyProductDialog` so more than one host can render THE SAME form.
 *
 * Two hosts need it now:
 *
 *   1. `CreateShopifyProductDialog` — the centred modal on the legacy Commerce
 *      page's Shopify tab (`/home/commerce/products?tab=shopify`), which is
 *      where these fields come from and still the canonical presentation.
 *   2. `commerce-v2/AddProductFlow` — the drawer whose "Publish to" picker now
 *      leads with Shopify, and therefore has to show Shopify's own fields
 *      rather than the WhatsApp-catalog ones.
 *
 * ── The one deliberate difference between the two hosts ────────────────────
 *
 * `singleVariant` collapses the Variants card into three fields — SKU, Price
 * and Stock — inside Basic Information. The drawer passes it; the dialog does
 * not, and keeps the full multi-variant card.
 *
 * This is a PRESENTATION mode over ONE state shape, not a second form: both
 * modes hold the same `variants[]` array and run the same submit, so single
 * mode is simply "edit `variants[0]`, render its three fields inline". Shopify
 * requires at least one variant carrying a price, so a product with no variant
 * UI still has to send one — the flat fields ARE that default variant, which
 * is exactly what a simple product is on Shopify. Keeping one array is what
 * lets the two modes share a create call rather than growing a second one.
 *
 * It is a hook plus a dumb fields component, NOT a second dialog, because the
 * two hosts frame it differently: the modal puts Status in its header and a
 * variant/image summary in its footer, the drawer puts Status inline under the
 * "Publish to" picker. Everything between those frames — media, basic info,
 * tags, variants — is identical and lives here exactly once.
 *
 * WHY A HOOK AND NOT PROP-DRILLED STATE: the submit is two round trips (create,
 * then a second call to attach per-variant images once the variant ids exist),
 * with a loading MESSAGE that changes between them. Handing a host raw setters
 * and asking it to re-derive that sequence is how the two copies drift.
 */

export interface ShopifyImageEntry {
  file: File;
  preview: string;
  base64: string;
}

export interface ShopifyVariant {
  option1: string;
  /**
   * Shopify's SKU is a VARIANT field, not a product one — two sizes of the
   * same shirt are two stock-keeping units. A single product-level SKU box
   * would have nowhere correct to go.
   */
  sku: string;
  price: string;
  inventory_quantity: number | '';
  image: ShopifyImageEntry | null;
}

export const emptyShopifyVariant = (): ShopifyVariant => ({
  option1: '',
  sku: '',
  price: '',
  inventory_quantity: 0,
  image: null,
});

export const SHOPIFY_STATUS_CONFIG: Record<
  string,
  { label: string; color: string }
> = {
  active: { label: 'Active', color: 'bg-emerald-500' },
  draft: { label: 'Draft', color: 'bg-slate-400' },
  archived: { label: 'Archived', color: 'bg-orange-400' },
};

/** Everything a host needs to render the fields and drive the submit. */
export interface ShopifyProductForm {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  vendor: string;
  setVendor: (v: string) => void;
  customVendor: string;
  setCustomVendor: (v: string) => void;
  productType: string;
  setProductType: (v: string) => void;
  customProductType: string;
  setCustomProductType: (v: string) => void;
  status: string;
  setStatus: (v: string) => void;
  tags: string[];
  tagInput: string;
  setTagInput: (v: string) => void;
  addTag: (raw: string) => void;
  removeTag: (index: number) => void;
  onTagKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  variants: ShopifyVariant[];
  addVariant: () => void;
  removeVariant: (index: number) => void;
  updateVariant: <K extends keyof ShopifyVariant>(
    index: number,
    field: K,
    value: ShopifyVariant[K],
  ) => void;
  setVariantImage: (index: number, files: FileList | null) => Promise<void>;
  mainImages: ShopifyImageEntry[];
  addMainFiles: (files: FileList | null) => Promise<void>;
  removeMainImage: (index: number) => void;
  vendors: string[];
  productTypes: string[];
  metaLoading: boolean;
  currencySymbol: string;
  loading: boolean;
  loadingMsg: string;
  /** Collapse the Variants card into flat SKU / Price / Stock fields. */
  singleVariant: boolean;
  /**
   * Name AND a price. Shopify refuses a variant without one, so a button that
   * accepted the click and then toasted would be reporting a rule it already
   * knew — the same "disabled until valid" call the drawer's port documents.
   */
  canSubmit: boolean;
  reset: () => void;
  /**
   * Resolves `{ ok: true, productId }` on success. The id is returned rather
   * than swallowed because the caller may need to act on the product it just
   * created — publishing it to the Meta catalog needs to target exactly that
   * product, not sweep the store.
   */
  submit: () => Promise<{ ok: boolean; productId?: string }>;
}

export function useShopifyProductForm({
  open,
  accountId,
  shopCurrency = 'USD',
  singleVariant = false,
}: {
  open: boolean;
  accountId: string | null;
  shopCurrency?: string;
  singleVariant?: boolean;
}): ShopifyProductForm {
  const currencySymbol = getCurrencySymbol(shopCurrency);

  const [loading, setLoading] = React.useState(false);
  const [loadingMsg, setLoadingMsg] = React.useState('Creating…');

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [vendor, setVendor] = React.useState('');
  const [customVendor, setCustomVendor] = React.useState('');
  const [productType, setProductType] = React.useState('');
  const [customProductType, setCustomProductType] = React.useState('');
  const [status, setStatus] = React.useState('active');
  const [tags, setTags] = React.useState<string[]>([]);
  const [tagInput, setTagInput] = React.useState('');
  const [variants, setVariants] = React.useState<ShopifyVariant[]>([
    emptyShopifyVariant(),
  ]);
  const [mainImages, setMainImages] = React.useState<ShopifyImageEntry[]>([]);

  const [vendors, setVendors] = React.useState<string[]>([]);
  const [productTypes, setProductTypes] = React.useState<string[]>([]);
  const [metaLoading, setMetaLoading] = React.useState(false);

  // Vendors / product types are the connected store's own lists, so they can
  // only be fetched once there is a store to ask. Both fall back to free text.
  React.useEffect(() => {
    if (!open || !accountId) return;
    setMetaLoading(true);
    shopifyApi
      .getStoreMeta(accountId)
      .then((meta) => {
        setVendors(meta.vendors || []);
        setProductTypes(meta.productTypes || []);
        // Default the vendor to the store's first one, but never overwrite a
        // value the user has already typed while the request was in flight.
        if (meta.vendors?.length) {
          setVendor((prev) => prev || meta.vendors[0]);
        }
      })
      .catch(() => {
        /* Free-text vendor / type inputs are the fallback — not an error. */
      })
      .finally(() => setMetaLoading(false));
  }, [open, accountId]);

  const reset = React.useCallback(() => {
    setTitle('');
    setDescription('');
    setVendor('');
    setCustomVendor('');
    setProductType('');
    setCustomProductType('');
    setStatus('active');
    setTags([]);
    setTagInput('');
    setVariants([emptyShopifyVariant()]);
    setMainImages([]);
  }, []);

  // ── Tags ───────────────────────────────────────────────────────────────────
  const addTag = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '');
    if (!t || tags.includes(t)) return;
    setTags((prev) => [...prev, t]);
    setTagInput('');
  };

  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length) {
      setTags((prev) => prev.slice(0, -1));
    }
  };

  const removeTag = (i: number) =>
    setTags((prev) => prev.filter((_, idx) => idx !== i));

  // ── Images ─────────────────────────────────────────────────────────────────
  const readFile = (file: File): Promise<ShopifyImageEntry> =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('Not an image'));
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        reject(new Error(`${file.name} exceeds 20MB`));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const r = reader.result as string;
        resolve({ file, preview: r, base64: r.split(',')[1] });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const addMainFiles = async (files: FileList | null) => {
    if (!files) return;
    const entries = await Promise.all(
      Array.from(files).map((f) =>
        readFile(f).catch((err) => {
          toast({ title: err.message, variant: 'destructive' });
          return null;
        }),
      ),
    );
    setMainImages((prev) => [
      ...prev,
      ...(entries.filter(Boolean) as ShopifyImageEntry[]),
    ]);
  };

  const removeMainImage = (index: number) =>
    setMainImages((prev) => prev.filter((_, idx) => idx !== index));

  // ── Variants ───────────────────────────────────────────────────────────────
  const updateVariant = <K extends keyof ShopifyVariant>(
    index: number,
    field: K,
    value: ShopifyVariant[K],
  ) =>
    setVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

  const setVariantImage = async (index: number, files: FileList | null) => {
    if (!files?.[0]) return;
    try {
      updateVariant(index, 'image', await readFile(files[0]));
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Not an image',
        variant: 'destructive',
      });
    }
  };

  const addVariant = () =>
    setVariants((prev) => [...prev, emptyShopifyVariant()]);

  const removeVariant = (i: number) =>
    setVariants((prev) =>
      prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev,
    );

  const hasPrice = variants.some((v) => v.price !== '');

  // ── Submit ─────────────────────────────────────────────────────────────────
  const submit = async (): Promise<{ ok: boolean; productId?: string }> => {
    if (!accountId) return { ok: false };
    if (!title.trim()) {
      toast({ title: 'Product name is required', variant: 'destructive' });
      return { ok: false };
    }
    if (!hasPrice) {
      toast({
        title: singleVariant
          ? 'Price is required'
          : 'At least one variant needs a price',
        variant: 'destructive',
      });
      return { ok: false };
    }

    setLoading(true);
    setLoadingMsg('Creating product…');
    try {
      const resolvedVendor = vendor === '__custom__' ? customVendor : vendor;
      const resolvedProductType =
        productType === '__custom__' ? customProductType : productType;

      const productData: Record<string, unknown> = {
        title: title.trim(),
        body_html: description,
        vendor: resolvedVendor,
        product_type: resolvedProductType,
        status,
        variants: variants.map((v) => ({
          option1: v.option1 || 'Default Title',
          // Omitted rather than sent blank: an empty string is a real SKU value
          // to Shopify and would collide across every variant left untyped.
          ...(v.sku.trim() ? { sku: v.sku.trim() } : {}),
          price: String(v.price),
          inventory_quantity:
            v.inventory_quantity === '' ? 0 : Number(v.inventory_quantity),
        })),
      };
      if (tags.length) productData.tags = tags.join(', ');
      if (mainImages.length) {
        productData.images = mainImages.map((img) => ({
          attachment: img.base64,
        }));
      }

      const shopifyProduct = await shopifyApi.createProduct(
        accountId,
        productData,
      );

      // Variant images can only be attached AFTER the create, because Shopify
      // assigns the variant ids they have to be keyed to.
      const variantImagesPayload = variants
        .map((v, i) => {
          if (!v.image) return null;
          const cv = shopifyProduct?.variants?.[i];
          if (!cv?.id) return null;
          return { attachment: v.image.base64, variant_ids: [cv.id] };
        })
        .filter(Boolean);

      if (variantImagesPayload.length && shopifyProduct?.id) {
        setLoadingMsg('Uploading variant images…');
        await shopifyApi.updateProduct(accountId, String(shopifyProduct.id), {
          images: variantImagesPayload,
        });
      }

      toast({
        variant: 'success',
        title: 'Product Created',
        description: `"${title}" has been added to Shopify.`,
      });
      const createdId =
        shopifyProduct?.id != null ? String(shopifyProduct.id) : undefined;
      reset();
      return { ok: true, productId: createdId };
    } catch (err) {
      showApiError(err, { fallback: { title: 'Create Failed' } });
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    vendor,
    setVendor,
    customVendor,
    setCustomVendor,
    productType,
    setProductType,
    customProductType,
    setCustomProductType,
    status,
    setStatus,
    tags,
    tagInput,
    setTagInput,
    addTag,
    removeTag,
    onTagKeyDown,
    variants,
    addVariant,
    removeVariant,
    updateVariant,
    setVariantImage,
    mainImages,
    addMainFiles,
    removeMainImage,
    vendors,
    productTypes,
    metaLoading,
    currencySymbol,
    loading,
    loadingMsg,
    singleVariant,
    canSubmit: Boolean(title.trim()) && hasPrice,
    reset,
    submit,
  };
}

/** The status picker, so both hosts render the same control in their own slot. */
export function ShopifyStatusSelect({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={cn('h-8 text-xs border-slate-200 w-28 font-medium', className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(SHOPIFY_STATUS_CONFIG).map(([val, cfg]) => (
          <SelectItem key={val} value={val} className="text-xs">
            <div className="flex items-center gap-2">
              <span className={cn('w-1.5 h-1.5 rounded-full', cfg.color)} />
              {cfg.label}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Media → Basic info → Tags → Variants. The whole body of the Shopify create
 * form, with no dialog or drawer chrome of its own.
 */
export function ShopifyProductFormFields({
  form,
  className,
}: {
  form: ShopifyProductForm;
  className?: string;
}) {
  const [dragging, setDragging] = React.useState(false);
  /**
   * The file inputs are driven by real <label htmlFor>, not a programmatic
   * click on a `display:none` input. That older shape depends on the browser
   * carrying user activation through a synthetic click on an element that is
   * not in the layout, which Safari/iOS and confined builds (snap/flatpak,
   * embedded webviews) can refuse outright — inside a focus-trapped dialog or
   * drawer most of all, which is exactly where both hosts render this. A label
   * pointing at a RENDERED (sr-only) input hands the job back to the browser.
   */
  const fileInputId = React.useId();
  const variantInputId = (index: number) => `${fileInputId}-variant-${index}`;

  return (
    <div className={cn('space-y-6', className)}>
      {/* ── Product Media ───────────────────────────────────────────────── */}
      <section>
        <SectionLabel
          icon={<ImageIcon className="h-4 w-4" />}
          title="Product Media"
          hint="First image is the primary thumbnail"
        />
        {form.mainImages.length === 0 ? (
          <>
            <input
              id={fileInputId}
              type="file"
              accept="image/*"
              multiple
              className="peer sr-only"
              onChange={(e) => form.addMainFiles(e.target.files)}
            />
            <DropZone
              htmlFor={fileInputId}
              dragging={dragging}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                form.addMainFiles(e.dataTransfer.files);
              }}
            />
          </>
        ) : (
          <div
            className={cn(
              'rounded-xl border-2 border-dashed p-3 transition-colors',
              dragging ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200',
            )}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              form.addMainFiles(e.dataTransfer.files);
            }}
          >
            <div className="grid grid-cols-5 gap-2">
              {form.mainImages.map((img, i) => (
                <div
                  key={i}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm"
                >
                  <img
                    src={img.preview}
                    alt={img.file.name}
                    className="w-full h-full object-cover"
                  />
                  {i === 0 && (
                    <div className="absolute top-1.5 left-1.5">
                      <Badge className="text-[9px] px-1.5 py-0 h-4 bg-black/70 text-white border-0 hover:bg-black/70">
                        Primary
                      </Badge>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      form.removeMainImage(i);
                    }}
                    className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label
                htmlFor={fileInputId}
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors group peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
              >
                <Plus className="h-5 w-5 text-slate-400 group-hover:text-emerald-500" />
                <span className="text-[10px] text-slate-400 group-hover:text-emerald-500 mt-1">
                  Add
                </span>
              </label>
            </div>
            <input
              id={fileInputId}
              type="file"
              accept="image/*"
              multiple
              className="peer sr-only"
              onChange={(e) => form.addMainFiles(e.target.files)}
            />
          </div>
        )}
      </section>

      {/* ── Basic Info ──────────────────────────────────────────────────── */}
      <section>
        <SectionLabel icon={<Package className="h-4 w-4" />} title="Basic Information" />
        <div className="space-y-4 bg-slate-50 rounded-xl p-4 border border-slate-100">
          <div>
            <Label className="text-xs font-medium text-slate-600">
              Product Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={form.title}
              onChange={(e) => form.setTitle(e.target.value)}
              placeholder="e.g. Classic Cotton T-Shirt"
              className="mt-1 bg-white"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => form.setDescription(e.target.value)}
              rows={3}
              placeholder="Describe your product…"
              className="mt-1 bg-white resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Vendor — dropdown from store if available, else free text */}
            <div>
              <Label className="text-xs font-medium text-slate-600">Vendor</Label>
              {form.vendors.length > 0 ? (
                <Select value={form.vendor} onValueChange={form.setVendor}>
                  <SelectTrigger className="mt-1 bg-white h-9 text-sm">
                    <SelectValue
                      placeholder={form.metaLoading ? 'Loading…' : 'Select vendor'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {form.vendors.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">+ Enter custom…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.vendor}
                  onChange={(e) => form.setVendor(e.target.value)}
                  placeholder={form.metaLoading ? 'Loading…' : 'Brand name'}
                  className="mt-1 bg-white"
                />
              )}
              {form.vendor === '__custom__' && (
                <Input
                  autoFocus
                  value={form.customVendor}
                  onChange={(e) => form.setCustomVendor(e.target.value)}
                  placeholder="Enter vendor name"
                  className="mt-2 bg-white"
                />
              )}
            </div>

            {/* Product Type / Category */}
            <div>
              <Label className="text-xs font-medium text-slate-600">
                Category / Type
              </Label>
              {form.productTypes.length > 0 ? (
                <Select
                  value={form.productType || '__none__'}
                  onValueChange={(v) =>
                    form.setProductType(v === '__none__' ? '' : v)
                  }
                >
                  <SelectTrigger className="mt-1 bg-white h-9 text-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {form.productTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">+ Enter custom…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.productType}
                  onChange={(e) => form.setProductType(e.target.value)}
                  placeholder={form.metaLoading ? 'Loading…' : 'e.g. Clothing'}
                  className="mt-1 bg-white"
                />
              )}
              {form.productType === '__custom__' && (
                <Input
                  autoFocus
                  value={form.customProductType}
                  onChange={(e) => form.setCustomProductType(e.target.value)}
                  placeholder="Enter product type"
                  className="mt-2 bg-white"
                />
              )}
            </div>
          </div>

          {/*
            Single-variant mode: the product's one default variant, edited
            inline. `variants[0]` is guaranteed to exist — the state is seeded
            with `emptyShopifyVariant()` and `removeVariant` refuses to drop
            the last row — so there is no empty case to guard.
          */}
          {form.singleVariant && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">SKU</Label>
                <Input
                  value={form.variants[0].sku}
                  onChange={(e) => form.updateVariant(0, 'sku', e.target.value)}
                  placeholder="WN-ABC-01"
                  className="mt-1 bg-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">
                  Price ({form.currencySymbol}){' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.variants[0].price}
                  onChange={(e) => form.updateVariant(0, 'price', e.target.value)}
                  placeholder="0.00"
                  className="mt-1 bg-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">
                  Stock
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.variants[0].inventory_quantity}
                  onChange={(e) =>
                    form.updateVariant(
                      0,
                      'inventory_quantity',
                      e.target.value === '' ? '' : parseInt(e.target.value, 10),
                    )
                  }
                  placeholder="0"
                  className="mt-1 bg-white h-9 text-sm"
                />
              </div>
            </div>
          )}

          {/* Tag chip input */}
          <div>
            <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
              <Tag className="h-3 w-3" /> Tags
              <span className="font-normal text-slate-400">
                — press Enter or comma to add
              </span>
            </Label>
            <TagChipInput form={form} />
          </div>
        </div>
      </section>

      {/* ── Variants ────────────────────────────────────────────────────── */}
      {!form.singleVariant && (
        <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-800">Variants</h3>
            <Badge variant="outline" className="text-xs">
              {form.variants.length}
            </Badge>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={form.addVariant}
            className="h-8 text-xs border-slate-200"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Variant
          </Button>
        </div>

        <div className="space-y-3">
          {form.variants.map((variant, index) => (
            <div
              key={index}
              className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm"
            >
              {/* Variant header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className="text-xs font-medium text-slate-600">
                    {variant.option1 || `Variant ${index + 1}`}
                  </span>
                  {variant.price && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {form.currencySymbol}
                      {variant.price}
                    </Badge>
                  )}
                </div>
                {form.variants.length > 1 && (
                  <button
                    type="button"
                    onClick={() => form.removeVariant(index)}
                    className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Variant body — image first, then fields */}
              <div className="p-4">
                <div className="flex items-center gap-4">
                  <div className="shrink-0">
                    {variant.image ? (
                      <div className="relative group w-16 h-16 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                        <img
                          src={variant.image.preview}
                          alt="Variant"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => form.updateVariant(index, 'image', null)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor={variantInputId(index)}
                        className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors group peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
                      >
                        <ImageIcon className="h-5 w-5 text-slate-300 group-hover:text-emerald-400" />
                        <span className="text-[9px] text-slate-400 group-hover:text-emerald-500 mt-0.5">
                          Image
                        </span>
                      </label>
                    )}
                    <input
                      id={variantInputId(index)}
                      type="file"
                      accept="image/*"
                      className="peer sr-only"
                      onChange={(e) => form.setVariantImage(index, e.target.files)}
                    />
                    {variant.image && (
                      <label
                        htmlFor={variantInputId(index)}
                        className="text-[10px] text-emerald-600 hover:underline mt-1 block text-center w-16 cursor-pointer peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
                      >
                        Change
                      </label>
                    )}
                  </div>

                  {/* Fields */}
                  <div className="flex-1 grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                        Option
                      </Label>
                      <Input
                        value={variant.option1}
                        onChange={(e) =>
                          form.updateVariant(index, 'option1', e.target.value)
                        }
                        placeholder="S / Red / 1kg"
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                        SKU
                      </Label>
                      <Input
                        value={variant.sku}
                        onChange={(e) =>
                          form.updateVariant(index, 'sku', e.target.value)
                        }
                        placeholder="WN-ABC-01"
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                        Price <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative mt-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                          {form.currencySymbol}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={variant.price}
                          onChange={(e) =>
                            form.updateVariant(index, 'price', e.target.value)
                          }
                          placeholder="0.00"
                          className="h-9 text-sm pl-7"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                        Stock
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={variant.inventory_quantity}
                        onChange={(e) =>
                          form.updateVariant(
                            index,
                            'inventory_quantity',
                            e.target.value === ''
                              ? ''
                              : parseInt(e.target.value, 10),
                          )
                        }
                        placeholder="0"
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}

/** One-line "3 images · 2 variants · 1 tag" summary both hosts can show. */
export function shopifyProductFormSummary(form: ShopifyProductForm): string {
  const parts: string[] = [];
  if (form.mainImages.length > 0) {
    parts.push(
      `${form.mainImages.length} image${form.mainImages.length > 1 ? 's' : ''}`,
    );
  }
  // A "1 variant" chip under a form with no variant card describes something
  // the user cannot see.
  if (!form.singleVariant) {
    parts.push(
      `${form.variants.length} variant${form.variants.length > 1 ? 's' : ''}`,
    );
  }
  if (form.tags.length > 0) {
    parts.push(`${form.tags.length} tag${form.tags.length > 1 ? 's' : ''}`);
  }
  return parts.join(' · ');
}

function TagChipInput({ form }: { form: ShopifyProductForm }) {
  const tagInputRef = React.useRef<HTMLInputElement>(null);
  return (
    <div
      className="mt-1 min-h-[38px] flex flex-wrap gap-1.5 items-center px-3 py-2 rounded-md border border-input bg-white cursor-text"
      onClick={() => tagInputRef.current?.focus()}
    >
      {form.tags.map((t, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs px-2 py-0.5"
        >
          {t}
          <button
            type="button"
            onClick={() => form.removeTag(i)}
            className="hover:text-red-500 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        ref={tagInputRef}
        value={form.tagInput}
        onChange={(e) => form.setTagInput(e.target.value)}
        onKeyDown={form.onTagKeyDown}
        onBlur={() => form.tagInput.trim() && form.addTag(form.tagInput)}
        placeholder={form.tags.length === 0 ? 'summer, sale, new-arrival…' : ''}
        className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder:text-muted-foreground"
      />
    </div>
  );
}

function SectionLabel({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-slate-400">{icon}</span>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

/** A <label>, not a clickable div — see the note on `fileInputId` above. */
function DropZone({
  dragging,
  htmlFor,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}: {
  dragging: boolean;
  htmlFor: string;
  onDragOver: React.DragEventHandler;
  onDragLeave: React.DragEventHandler;
  onDrop: React.DragEventHandler;
  children?: React.ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200',
        'peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2',
        dragging
          ? 'border-emerald-400 bg-emerald-50 scale-[1.01]'
          : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50',
      )}
    >
      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
        <Upload className="h-5 w-5 text-slate-400" />
      </div>
      <p className="text-sm font-medium text-slate-700">
        Drop images here or click to browse
      </p>
      <p className="text-xs text-slate-400 mt-1">
        PNG, JPG, WEBP · Max 20MB each · Multiple allowed
      </p>
      {children}
    </label>
  );
}
